import React, { useState } from "react";
import { format, parseISO, startOfToday } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Visual date picker (calendar popup) that emits a YYYY-MM-DD string.
// Past dates (and anything before `min` / after `max`) are greyed out & unselectable.
export function DatePicker({ value, onChange, min, max, placeholder = "Select date", testid, className, range, disabledDates }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const minDate = min ? (typeof min === "string" ? parseISO(min) : min) : startOfToday();
  const maxDate = max ? (typeof max === "string" ? parseISO(max) : max) : undefined;

  const disabled = [{ before: minDate }];
  if (maxDate) disabled.push({ after: maxDate });
  if (Array.isArray(disabledDates)) {
    disabledDates.forEach((d) => { if (d) disabled.push(parseISO(d)); });
  }

  // Highlight the selected rental period (pickup → drop-off) across the calendar.
  const rf = range?.from ? parseISO(range.from) : undefined;
  const rt = range?.to ? parseISO(range.to) : undefined;
  const modifiers = {};
  const modifiersClassNames = {};
  if (rf && rt && rt > rf) {
    modifiers.rangeInside = { after: rf, before: rt };
    modifiers.rangeEdge = [rf, rt];
    modifiersClassNames.rangeInside = "bg-cyan-500/20 text-cyan-100 !rounded-none";
    modifiersClassNames.rangeEdge = "bg-gradient-to-br from-cyan-500 to-blue-600 text-white";
  }

  const handleSelect = (d) => {
    if (!d) return;
    onChange(format(d, "yyyy-MM-dd"));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={cn(
            "w-full flex items-center gap-2 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm outline-none focus:border-cyan-400/50 hover:border-cyan-400/40 transition-colors",
            value ? "text-white" : "text-slate-500",
            className
          )}
        >
          <CalendarDays className="w-4 h-4 text-cyan-400 shrink-0" />
          <span className="truncate">{selected ? format(selected, "EEE, MMM d, yyyy") : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#0B0E1B] border border-[#1D2640] rounded-2xl shadow-2xl z-[80]"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          defaultMonth={selected || rf || minDate}
          disabled={disabled}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          initialFocus
          classNames={{
            caption_label: "text-sm font-semibold text-white",
            nav_button: "h-7 w-7 bg-transparent p-0 text-slate-300 hover:text-cyan-400 rounded-md border border-[#1D2640]",
            head_cell: "text-slate-500 rounded-md w-8 font-normal text-[0.8rem]",
            day: "h-8 w-8 p-0 font-normal text-slate-200 rounded-md hover:bg-[#141A2E] aria-selected:opacity-100",
            day_selected: "bg-gradient-to-br from-cyan-500 to-blue-600 text-white hover:from-cyan-500 hover:to-blue-600 focus:from-cyan-500 focus:to-blue-600",
            day_today: "border border-cyan-400/50 text-cyan-300",
            day_disabled: "text-slate-700 opacity-40 line-through cursor-not-allowed",
            day_outside: "text-slate-700 opacity-40",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
